import { useState } from "react";
import useReveal from "@/hooks/useReveal";
import { useLang } from "@/i18n/LangContext";

// Real-life PrivacyGlass application photos. Layout uses a 12-col asymmetric
// grid: feature + companion row, a 3-up row, then a 2-up row (7 cards total).
const PHOTOS = [
  { src: "/applications/jet-lounge.jpg", span: "md:col-span-7", h: "h-[420px] md:h-[560px]" },
  { src: "/applications/boardroom.jpg", span: "md:col-span-5", h: "h-[420px] md:h-[560px]" },
  { src: "/applications/private-office.jpg", span: "md:col-span-4", h: "h-[300px] md:h-[380px]" },
  { src: "/applications/saloon.jpg", span: "md:col-span-4", h: "h-[300px] md:h-[380px]" },
  { src: "/applications/gym.jpg", span: "md:col-span-4", h: "h-[300px] md:h-[380px]" },
  { src: "/applications/office-partition.jpg", span: "md:col-span-6", h: "h-[340px] md:h-[460px]" },
  { src: "/applications/folding-door.jpg", span: "md:col-span-6", h: "h-[340px] md:h-[460px]" },
];

export default function Applications() {
  const { t } = useLang();
  const tiles = t("applications.tiles");
  const { ref, className } = useReveal();
  const [expanded, setExpanded] = useState(null);

  return (
    <section
      id="applications"
      data-testid="applications-section"
      className="pt-24 md:pt-32 lg:pt-40 pb-10 md:pb-12 lg:pb-14 bg-[#F5F5F7]"
    >
      <div ref={ref} className={`${className} max-w-7xl mx-auto px-6 md:px-10`}>
        <div className="max-w-3xl mb-14 md:mb-20">
          <h2
            data-testid="applications-title"
            className="font-display font-light tracking-tight text-4xl md:text-5xl lg:text-6xl text-[#1D1D1F] leading-[1.05]"
          >
            {t("applications.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          {PHOTOS.map((photo, i) => {
            const tile = tiles[i];
            const isExpanded = expanded === i;
            return (
              <figure
                key={photo.src}
                data-testid={`application-card-${i}`}
                onClick={() => setExpanded(isExpanded ? null : i)}
                className={`group relative overflow-hidden rounded-3xl bg-black cursor-zoom-${
                  isExpanded ? "out" : "in"
                } transition-[grid-column,height] duration-500 ease-out ${
                  isExpanded ? "md:col-span-12 h-auto" : `${photo.span} ${photo.h}`
                }`}
              >
                <img
                  src={photo.src}
                  alt={tile.name}
                  draggable="false"
                  loading="lazy"
                  className={`block w-full transition-all duration-700 ease-out ${
                    isExpanded
                      ? "h-auto max-h-[85vh] object-contain bg-black"
                      : "absolute inset-0 h-full object-cover group-hover:scale-[1.05]"
                  }`}
                />
                {!isExpanded && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent pointer-events-none" />
                )}
                <figcaption
                  className={`text-white pointer-events-none ${
                    isExpanded
                      ? "relative bg-black/90 px-7 py-6 md:px-8"
                      : "absolute bottom-7 left-7 right-7 md:bottom-8 md:left-8 md:right-8"
                  }`}
                >
                  <h3 className="font-display font-light text-2xl md:text-3xl lg:text-[2rem] tracking-tight leading-[1.1]">
                    {tile.name}
                  </h3>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
